import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { HistoryEntryRow } from "../HistoryEntryRow";
import { HistoryPager } from "../HistoryPager";
import { useHistoryEntries } from "../hooks/useHistoryEntries";
import { HISTORY_PAGE_SIZE, NEWEST_PAGE } from "../constants";
import type { HistoryPageCursor } from "../types";
import type { HistoryListProps } from "./types";

export const HistoryList = ({
  projectId,
  subject,
  currentUserEmail,
  revertingEntryId,
  onRevert,
}: HistoryListProps) => {
  const { t } = useTranslation();
  const [cursor, setCursor] = useState<HistoryPageCursor>(NEWEST_PAGE);

  const list = useHistoryEntries({ projectId, subject, cursor });

  // Leaving the newest page freezes the snapshot it showed, so older pages line up with it.
  const showOlder = useCallback(() => {
    setCursor({
      page: cursor.page + 1,
      snapshotMaxId: cursor.snapshotMaxId ?? list.page?.snapshotMaxId ?? null,
    });
  }, [cursor, list.page]);

  const showNewer = useCallback(() => {
    setCursor(
      cursor.page <= 2
        ? NEWEST_PAGE
        : { page: cursor.page - 1, snapshotMaxId: cursor.snapshotMaxId },
    );
  }, [cursor]);

  const showNewest = useCallback(() => {
    setCursor(NEWEST_PAGE);
  }, []);

  const isLoading = list.isLoading || list.isSwitchingPage;
  const entries = list.isSwitchingPage ? [] : (list.page?.entries ?? []);
  // The live newest page refreshes after every change, so only frozen pages offer the newer entries.
  const isFrozen = cursor.snapshotMaxId !== null && !list.isSwitchingPage;
  const newerCount = isFrozen ? (list.page?.newerCount ?? 0) : 0;
  const pageCount = Math.ceil((list.page?.total ?? 0) / HISTORY_PAGE_SIZE);

  return (
    <>
      <HistoryPager
        page={cursor.page}
        pageCount={pageCount}
        onNewer={showNewer}
        onOlder={showOlder}
      />
      {newerCount > 0 && (
        <button
          type="button"
          onClick={showNewest}
          className="border-b border-indigo-100 bg-indigo-50 px-4 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
        >
          {t("smartSurveyor.history.showNewer", { count: newerCount })}
        </button>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto px-4">
        {list.error !== null && (
          <p className="py-4 text-center text-sm text-red-600">
            {t("smartSurveyor.history.loadError")}
          </p>
        )}
        {isLoading && (
          <div className="flex justify-center py-6">
            <Loader2 size={20} className="animate-spin text-gray-400" />
          </div>
        )}
        {!isLoading && list.error === null && entries.length === 0 && (
          <p className="py-4 text-center text-sm text-gray-500">
            {t("smartSurveyor.history.empty")}
          </p>
        )}
        <ul>
          {entries.map((entry) => (
            <HistoryEntryRow
              key={entry.id}
              entry={entry}
              currentUserEmail={currentUserEmail}
              revertingEntryId={revertingEntryId}
              onRevert={onRevert}
            />
          ))}
        </ul>
      </div>
    </>
  );
};
